import { Injectable, signal } from '@angular/core';

export class NotesDialogData {
  constructor(
    readonly orderId: string,
    readonly title: string,
    readonly agentId?: string,
    readonly subtitle?: string,
  ) { }
}

/**
 * Class used to instantiate a proper configuration object used as a parameter to `NotesDialogService.openNotes()`
 *
 * Example usage:
 *
 *
 *```
 * const service = new NotesDialogService();
 * const config = new NotesDialogServiceConfig({
 *   orderId: 'ORDERID',
 *   agentId: 'AGENTID',
 *   title: 'TITLE',
 *   subtitle: 'SUBTITLE',
 * });
 * service.openNotes(config);
 *```
 *
 *
 */
export class NotesDialogServiceConfig {
  // The order ID for order notes
  orderId!: string;
  // The agent/user ID for agent notes
  agentId?: string;
  // The title of the Dialog
  title?: string;
  // The subtitle of the Dialog
  subtitle!: string;

  constructor(value?: Partial<NotesDialogServiceConfig>) {
    Object.assign(this, value);
  }
}

/**
 * Handles the creation of the Notes Drawer component by managing drawer state
 */
@Injectable({ providedIn: 'root' })
export class NotesDialogService {
  // Signal to control drawer open/close state
  readonly isOpen = signal(false);
  readonly config = signal<NotesDialogData | null>(null);

  /**
   * Opens the notes drawer with the required parameters.
   *
   * If no title is provided, it defaults to "Notes".
   * However, values for `orderId` and `agentId` are required.
   *
   * Example 1:
   *
   * ```
   * openNotes({ title: 'TITLE', orderId: 'ORDERID', agentId: 'AGENTID', subtitle: 'SUBTITLE' });
   * ```
   *
   * Example 2:
   * ```
   * const config = new NotesDialogServiceConfig({ orderId: 'ORDERID', agentId: 'AGENTID', subtitle: 'SUBTITLE' });
   * openNotes(config);
   * ```
   *
   *
   */
  openNotes(config: NotesDialogServiceConfig) {
    const { orderId, agentId, subtitle, title } = config;
    this.config.set(new NotesDialogData(orderId, title || 'Notes', agentId, subtitle));
    this.isOpen.set(true);
  }

  /**
   * Closes the notes drawer
   */
  closeNotes() {
    this.isOpen.set(false);
  }

  /**
   * If the drawer is currently open, returns `true`. Otherwise, returns `false`.
   */
  isNotesOpen() {
    return this.isOpen();
  }
}
